// <purpose>DTO selectors for the Distribution hero — funnel buckets, per-category volume & Neuanalyse rate from ActiveDistribution aggregate.</purpose>
// <constraints>All windows are server-computed; selectors are pure mappings over the DTO. No client-side time logic. NeuanalyseStat interface is public (used by DistributionHero).</constraints>
import type { ActiveDistribution, DistWindow } from '../serverTypes';
import type { ActiveBucket } from './lifecycleBuckets';

export type FrequencyBucket = 'one' | 'two' | 'threePlus';
export type TimeFilter = '7d' | '30d' | 'thisWeek' | 'thisMonth';

export interface NeuanalyseStat { volume: Record<string, number>; count: Record<string, number>; rate: Record<string, number>; }

// Why: TimeFilter values are a strict subset of DistWindow; 'all' is the null-default sentinel.
const toWindow = (tf: TimeFilter | null): DistWindow => tf ?? 'all';

export function funnelActive(
  d: ActiveDistribution,
  tf: TimeFilter | null,
): { uploads: number; offen: number; entschieden: number } {
  return d.funnel[toWindow(tf)];
}

export function funnelRejections(
  d: ActiveDistribution,
  tf: TimeFilter | null,
): { one: number; two: number; threePlus: number } {
  return d.reanalyseFunnel[toWindow(tf)];
}

export function categoryVolume(
  d: ActiveDistribution,
  categoryKeys: string[],
  o: { lifecycle: ActiveBucket | null; timeFilter: TimeFilter | null },
): Record<string, number> {
  const w = toWindow(o.timeFilter);
  const lc = o.lifecycle ?? 'all';
  const raw = d.volume[w]?.[lc] ?? {};
  // Seed every known key to 0 so the treemap/zero-rail never sees undefined.
  const result: Record<string, number> = {};
  for (const k of categoryKeys) result[k] = raw[k] ?? 0;
  return result;
}

export function neuanalyse(
  d: ActiveDistribution,
  categoryKeys: string[],
  o: { frequency: FrequencyBucket | null; timeFilter: TimeFilter | null },
): NeuanalyseStat {
  const w = toWindow(o.timeFilter);
  const freq = o.frequency ?? 'all';
  // volume is always from the 'all' lifecycle slice (Neuanalyse is per-category total, not lifecycle-filtered).
  const rawVol = d.volume[w]?.['all'] ?? {};
  const rawCount = d.reanalyseCount[w]?.[freq] ?? {};
  const volume: Record<string, number> = {};
  const count: Record<string, number> = {};
  const rate: Record<string, number> = {};
  for (const k of categoryKeys) {
    volume[k] = rawVol[k] ?? 0;
    count[k] = rawCount[k] ?? 0;
    rate[k] = volume[k] > 0 ? count[k] / volume[k] : 0;
  }
  return { volume, count, rate };
}
